import { DateTime as DT } from 'luxon';
import { TaskTypeStatus, type TaskType } from '@econtractor/types';
import {
    isoDateToEndMillis,
    isoDateToStartMillis,
    millisToIsoDate,
    millisToTime,
    resolveDateEndMillis,
    resolveDateStartMillis,
} from '../../NewTask/helpers/initNewTaskStruct';
import { BINDING_HISTORY_MARKER } from './taskBindingHelpers';
import { USERS_HISTORY_MARKER } from './taskUsersHelpers';

/** Службовий маркер у reject_comment для подій зміни терміну */
export const TERM_HISTORY_MARKER = '__TERM__';
/** Коментар виконавця після діапазону, в одному рядку з маркером терміну */
export const TERM_NOTE_MARKER = '__NOTE__';
export const REMIND_HISTORY_MARKER = '__REMIND__';

export interface TaskDueDateValues {
    multiDay: boolean;
    withTime: boolean;
    dateStart: number;
    dateEnd: number;
    startTime: string;
    endTime: string;
}

/** Контролер може змінювати термін лише для NEW / INWORK (не видалених) */
export const canEditTaskTerm = (task: TaskType, userId: number): boolean => {
    if (!(userId > 0)) return false;
    if (Boolean(Number(task.taskDeleted))) return false;
    if (userId !== Number(task.taskController)) return false;
    const status = Number(task.taskStatus);
    return status === TaskTypeStatus.NEW || status === TaskTypeStatus.INWORK;
};

/** Виконавець змінює дату кінця лише в роботі і якщо дозвіл увімкнено при створенні */
export const canExecutorChangeTerm = (task: TaskType, userId: number): boolean => {
    if (!(userId > 0)) return false;
    if (Boolean(Number(task.taskDeleted))) return false;
    if (!task.executorCanChangeTerm) return false;
    if (userId !== Number(task.taskExecutor)) return false;
    return Number(task.taskStatus) === TaskTypeStatus.INWORK;
};

export const dueDatesFromTask = (task: TaskType): TaskDueDateValues => {
    const withTime = Boolean(task.withTime);
    const endTimeRaw = String(task.endTime || '').trim();
    const endTime = endTimeRaw
        ? endTimeRaw.slice(0, 5)
        : millisToTime(task.dateEnd);
    const dateStart = Number(task.dateStart) || DT.now().setZone('local').startOf('day').toMillis();

    return {
        multiDay: Boolean(task.multiDay),
        withTime,
        dateStart,
        dateEnd: Number(task.dateEnd) || DT.now().setZone('local').endOf('day').toMillis(),
        startTime: withTime ? millisToTime(dateStart, '00:00') : '00:00',
        endTime: endTime || '18:00',
    };
};

export const normalizeDueDateValues = (values: TaskDueDateValues) => {
    const dateEnd = resolveDateEndMillis(values.dateEnd, values.withTime, values.endTime);
    const dateStart = resolveDateStartMillis(
        values.dateStart,
        dateEnd,
        values.multiDay,
        values.withTime,
        values.startTime,
    );

    return {
        multiDay: values.multiDay,
        withTime: values.withTime,
        dateStart,
        dateEnd,
        endTime: values.withTime ? String(values.endTime || '18:00').trim() || null : null,
    };
};

/** Як у таблиці: start/end з часом якщо withTime */
const formatTermPoint = (millis: number, withTime: boolean): string => {
    const dt = DT.fromMillis(millis, { zone: 'local' });
    if (!dt.isValid) return '—';
    return withTime ? dt.toFormat('dd.MM.yyyy HH:mm') : dt.toFormat('dd.MM.yyyy');
};

/** Коментар історії: `15.07.2026 00:00 → 20.07.2026 18:00` (в один рядок) */
export const buildTermHistoryComment = (
    values: ReturnType<typeof normalizeDueDateValues>,
): string => {
    const startLabel = formatTermPoint(values.dateStart, values.withTime);
    const endLabel = formatTermPoint(values.dateEnd, values.withTime);
    return `${TERM_HISTORY_MARKER}\n${startLabel} → ${endLabel}`;
};

const splitTermNote = (body: string): { text: string; note?: string } => {
    const index = body.indexOf(TERM_NOTE_MARKER);
    if (index < 0) return { text: body.trim() };
    const note = body.slice(index + TERM_NOTE_MARKER.length).trim();
    return {
        text: body.slice(0, index).trim(),
        ...(note ? { note } : {}),
    };
};

export const parseHistoryComment = (
    raw: string | null | undefined,
): { kind: 'term' | 'users' | 'binding' | 'remind' | 'plain'; text: string; note?: string } => {
    const value = String(raw || '');
    const termPrefix = `${TERM_HISTORY_MARKER}\n`;
    if (value.startsWith(termPrefix)) {
        return { kind: 'term', ...splitTermNote(value.slice(termPrefix.length)) };
    }
    if (value.startsWith(TERM_HISTORY_MARKER)) {
        return {
            kind: 'term',
            ...splitTermNote(value.slice(TERM_HISTORY_MARKER.length).replace(/^\n/, '')),
        };
    }

    const usersPrefix = `${USERS_HISTORY_MARKER}\n`;
    if (value.startsWith(usersPrefix)) {
        return { kind: 'users', text: value.slice(usersPrefix.length) };
    }
    if (value.startsWith(USERS_HISTORY_MARKER)) {
        return { kind: 'users', text: value.slice(USERS_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    const bindingPrefix = `${BINDING_HISTORY_MARKER}\n`;
    if (value.startsWith(bindingPrefix)) {
        return { kind: 'binding', text: value.slice(bindingPrefix.length) };
    }
    if (value.startsWith(BINDING_HISTORY_MARKER)) {
        return {
            kind: 'binding',
            text: value.slice(BINDING_HISTORY_MARKER.length).replace(/^\n/, '').trim(),
        };
    }

    const remindPrefix = `${REMIND_HISTORY_MARKER}\n`;
    if (value.startsWith(remindPrefix)) {
        return { kind: 'remind', text: value.slice(remindPrefix.length) };
    }
    if (value.startsWith(REMIND_HISTORY_MARKER)) {
        return { kind: 'remind', text: value.slice(REMIND_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    return { kind: 'plain', text: value };
};

/** Дні включно: той самий календарний день = 1. */
export const inclusiveTermDays = (dateStart: number, dateEnd: number): number => {
    const start = DT.fromMillis(Number(dateStart) || 0, { zone: 'local' }).startOf('day');
    const end = DT.fromMillis(Number(dateEnd) || 0, { zone: 'local' }).startOf('day');
    if (!start.isValid || !end.isValid) return 1;
    const diff = Math.round(end.diff(start, 'days').days);
    return Math.max(1, diff + 1);
};

/** ISO-дата «Виконати до» для N днів включно від початку. */
export const isoDateFromTermDays = (dateStart: number, days: number): string => {
    const start = DT.fromMillis(Number(dateStart) || 0, { zone: 'local' }).startOf('day');
    const safeDays = Math.floor(Number(days));
    if (!start.isValid || !Number.isFinite(safeDays) || safeDays < 1) return '';
    return start.plus({ days: safeDays - 1 }).toISODate() || '';
};

/** Шаблон із терміном: кілька днів, без часу, кінець від поточного початку. */
export const applyTemplateTermDays = (
    dateStart: number,
    termDays: number,
): TaskDueDateValues | null => {
    const days = Math.floor(Number(termDays));
    if (!Number.isFinite(days) || days < 1) return null;
    const startMillis = isoDateToStartMillis(millisToIsoDate(dateStart), false, '00:00');
    const endMillis = isoDateToEndMillis(isoDateFromTermDays(startMillis ?? dateStart, days), false);
    if (startMillis == null || endMillis == null) return null;
    return {
        multiDay: true,
        withTime: false,
        dateStart: startMillis,
        dateEnd: endMillis,
        startTime: '00:00',
        endTime: '18:00',
    };
};

export const applyEndDateChange = (
    values: TaskDueDateValues,
    isoDate: string,
): Partial<TaskDueDateValues> => {
    const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
    if (endMillis == null) return {};
    if (!values.multiDay) {
        return {
            dateEnd: endMillis,
            dateStart: isoDateToStartMillis(isoDate, values.withTime, values.startTime)
                ?? values.dateStart,
        };
    }
    return { dateEnd: endMillis };
};

export const applyStartDateChange = (
    values: TaskDueDateValues,
    isoDate: string,
): Partial<TaskDueDateValues> => {
    const startMillis = isoDateToStartMillis(isoDate, values.withTime, values.startTime);
    if (startMillis == null) return {};

    if (values.multiDay && !values.withTime) {
        const days = inclusiveTermDays(values.dateStart, values.dateEnd);
        const endMillis = isoDateToEndMillis(isoDateFromTermDays(startMillis, days), false, values.endTime);
        return {
            dateStart: startMillis,
            ...(endMillis != null ? { dateEnd: endMillis } : {}),
        };
    }

    const next: Partial<TaskDueDateValues> = { dateStart: startMillis };
    const endStartOfDay = isoDateToStartMillis(millisToIsoDate(values.dateEnd));
    if (endStartOfDay != null && startMillis > endStartOfDay) {
        const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
        if (endMillis != null) next.dateEnd = endMillis;
    }
    return next;
};

export const applyWithTimeChange = (
    values: TaskDueDateValues,
    checked: boolean,
): Partial<TaskDueDateValues> => {
    const startTime = '00:00';
    const endIso = millisToIsoDate(values.dateEnd);
    const startIso = values.multiDay ? millisToIsoDate(values.dateStart) : endIso;
    const endMillis = isoDateToEndMillis(endIso, checked, values.endTime);
    const startMillis = isoDateToStartMillis(startIso, checked, startTime);
    return {
        withTime: checked,
        startTime,
        ...(startMillis != null ? { dateStart: startMillis } : {}),
        ...(endMillis != null ? { dateEnd: endMillis } : {}),
    };
};

export const applyStartTimeChange = (
    values: TaskDueDateValues,
    time: string,
): Partial<TaskDueDateValues> => {
    const startIso = values.multiDay
        ? millisToIsoDate(values.dateStart)
        : millisToIsoDate(values.dateEnd);
    const startMillis = isoDateToStartMillis(startIso, true, time);
    return {
        startTime: time,
        ...(startMillis != null ? { dateStart: startMillis } : {}),
    };
};

export const applyEndTimeChange = (
    values: TaskDueDateValues,
    time: string,
): Partial<TaskDueDateValues> => {
    const endIso = millisToIsoDate(values.dateEnd);
    const endMillis = isoDateToEndMillis(endIso, true, time);
    return {
        endTime: time,
        ...(endMillis != null ? { dateEnd: endMillis } : {}),
    };
};

export const applyMultiDayChange = (
    values: TaskDueDateValues,
    checked: boolean,
): Partial<TaskDueDateValues> => {
    if (!checked) {
        return {
            multiDay: false,
            dateStart: isoDateToStartMillis(
                millisToIsoDate(values.dateEnd),
                values.withTime,
                values.startTime,
            ) ?? values.dateStart,
        };
    }
    return { multiDay: true };
};
